import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { navData } from "../../data/mockData";

/* ── Footer Sub-Sections ── */

const FooterBrand = () => (
  <div className="space-y-4">
    <div className="inline-block rounded-xl bg-white px-3 py-2">
      <img src={navData.logo} alt={`${navData.brandName} ${navData.tagline}`} className="h-14 w-auto object-contain" data-testid="footer-logo-img" />
    </div>
    <p className="text-slate-400 text-sm leading-relaxed">
      Domestic and commercial cleaning in Cardiff, Barry and Penarth.
      Fully insured, DBS-checked, and dedicated to making your space spotless.
    </p>
  </div>
);

const FooterContact = () => (
  <div className="space-y-4">
    <h4 className="text-lg font-bold">Get In Touch</h4>
    <div className="space-y-3">
      <div>
        <a
          href={`tel:${navData.phoneLink}`}
          className="flex items-center gap-3 text-slate-400 hover:text-sky-400 transition-colors"
        >
          <Phone className="w-4 h-4 text-sky-500 flex-shrink-0" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-slate-300">Call NeatWave:</span>
            <span className="text-sm">{navData.phone}</span>
          </div>
        </a>
      </div>
      <a
        href="mailto:info@neatwavecleaning.co.uk"
        className="flex items-center gap-3 text-slate-400 hover:text-sky-400 transition-colors"
      >
        <Mail className="w-4 h-4 text-sky-500 flex-shrink-0" />
        <span className="text-sm">info@neatwavecleaning.co.uk</span>
      </a>
      <div className="flex items-start gap-3 text-slate-400">
        <MapPin className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
        <span className="text-sm" data-testid="footer-service-areas">
          Areas we cover:{" "}
          <Link to="/" className="hover:text-sky-400">Cardiff</Link>,{" "}
          <Link to="/cleaning-services-barry" className="hover:text-sky-400" data-testid="footer-area-barry">Barry</Link> and{" "}
          <Link to="/cleaning-services-penarth" className="hover:text-sky-400" data-testid="footer-area-penarth">Penarth</Link>
        </span>
      </div>
      <div className="flex items-center gap-3 text-slate-400 pt-2 border-t border-slate-800">
        <svg className="w-4 h-4 text-sky-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-xs text-slate-500">Opening Hours:</span>
          <span className="text-sm">Mon-Sun: 8:00 AM - 6:00 PM</span>
        </div>
      </div>
    </div>
  </div>
);

const FooterSocial = () => (
  <div className="space-y-4">
    <h4 className="text-lg font-bold">Follow Us</h4>
    <p className="text-slate-400 text-sm">
      Stay updated with our latest offers and cleaning tips!
    </p>
    <div className="space-y-3">
      <a
        href={navData.socials.facebook.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 group"
      >
        <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-sky-600 transition-colors">
          <Facebook className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
            Facebook
          </p>
          <p className="text-xs text-slate-500">{navData.socials.facebook.label}</p>
        </div>
      </a>
      <a
        href={navData.socials.instagram.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 group"
      >
        <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-pink-500 transition-colors">
          <Instagram className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
            Instagram
          </p>
          <p className="text-xs text-slate-500">@{navData.socials.instagram.label}</p>
        </div>
      </a>
    </div>
  </div>
);

const FooterBottom = () => (
  <div className="border-t border-slate-800 mt-12 pt-8">
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
      <p className="text-slate-500 text-sm">
        © {new Date().getFullYear()} NeatWave Cleaning. All rights reserved.
      </p>
      <div className="flex items-center gap-6">
        <a
          href="/about"
          className="text-slate-500 hover:text-sky-400 transition-colors text-sm"
        >
          About Us
        </a>
        <a
          href="/privacy-policy"
          className="text-slate-500 hover:text-sky-400 transition-colors text-sm"
        >
          Privacy Policy
        </a>
        <a
          href={navData.socials.facebook.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-500 hover:text-sky-500 transition-colors"
          aria-label="Facebook"
        >
          <Facebook className="w-5 h-5" />
        </a>
        <a
          href={navData.socials.instagram.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-500 hover:text-pink-500 transition-colors"
          aria-label="Instagram"
        >
          <Instagram className="w-5 h-5" />
        </a>
      </div>
    </div>
  </div>
);

const FOOTER_SERVICES = [
  ["Domestic Cleaning", "/domestic-cleaning-cardiff"],
  ["Deep Cleaning", "/deep-cleaning-cardiff"],
  ["End of Tenancy Cleaning", "/end-of-tenancy-cleaning-cardiff"],
  ["Landlord & Agent Cleaning", "/landlord-letting-agent-cleaning-cardiff"],
  ["Airbnb Cleaning", "/airbnb-cleaning-cardiff"],
  ["Commercial Cleaning", "/commercial-cleaning-cardiff"],
  ["Office Cleaning", "/office-cleaning-cardiff"],
  ["Oven Cleaning", "/oven-cleaning-cardiff"],
  ["Carpet Cleaning", "/carpet-cleaning-cardiff"],
  ["Laundry & Ironing", "/laundry-ironing-cardiff"],
];

const FooterServices = () => (
  <nav className="space-y-4" aria-label="Our services" data-testid="footer-services">
    <h4 className="text-lg font-bold">Our Services</h4>
    <ul className="space-y-2">
      {FOOTER_SERVICES.map(([label, to]) => (
        <li key={to}>
          <Link to={to} className="text-sm text-slate-400 transition-colors hover:text-sky-400">{label}</Link>
        </li>
      ))}
    </ul>
  </nav>
);

/* ── Main Footer ── */

const Footer = () => (
  <footer className="bg-slate-900 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        <FooterBrand />
        <FooterServices />
        <FooterContact />
        <FooterSocial />
      </div>
      <FooterBottom />
    </div>
  </footer>
);

export default Footer;
